// AuthService implementation

interface LoginCredentials {
  email: string;
  password: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
}

class AuthService {
  private token: string | null = null;
  private user: User | null = null;

  async login(credentials: LoginCredentials): Promise<User> {
    try {
      // In a real application, you would make an API call to authenticate the user
      // For now, we'll simulate authentication with mock data
      const mockUsers: User[] = [
        {
          id: 1,
          name: 'Admin User',
          email: 'admin@example.com',
          role: 'Admin',
          department: 'Administration'
        },
        {
          id: 2,
          name: 'Manager User',
          email: 'manager@example.com',
          role: 'Manager',
          department: 'Sales'
        },
        {
          id: 3,
          name: 'Employee User',
          email: 'employee@example.com',
          role: 'Employee',
          department: 'Sales'
        }
      ];

      const user = mockUsers.find(
        u => u.email === credentials.email && credentials.password === 'password'
      );

      if (!user) {
        throw new Error('Invalid credentials');
      }

      // Simulate token generation
      this.token = 'mock-jwt-token';
      this.user = user;
      
      // Store in localStorage for persistence
      localStorage.setItem('token', this.token);
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch (error) {
      throw new Error('Login failed');
    }
  }

  logout(): void {
    this.token = null;
    this.user = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  getCurrentUser(): User | null {
    if (!this.user) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        this.user = JSON.parse(userStr);
      }
    }
    return this.user;
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.role === role : false;
  }

  hasAnyRole(roles: string[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  canViewDepartment(department: string): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Admin can view all departments
    if (user.role === 'Admin') return true;
    
    // Manager can view their own department
    if (user.role === 'Manager') return user.department === department;
    
    // Employee can only view their own data (handled at component level)
    return false;
  }

  canEditUser(userId: number): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;
    
    // Admin can edit all users
    if (user.role === 'Admin') return true;
    
    // Manager can edit users in their department
    if (user.role === 'Manager') {
      // In a real app, you'd check if the user being edited is in the same department
      return true;
    }
    
    // Employee can only edit their own data
    return user.id === userId;
  }
}

const authService = new AuthService();
export default authService;